import { z } from 'zod';
import { EntityIdSchema, IsoDateTimeSchema } from './common';

/**
 * Tipo de registro de asistencia.
 */
export const AsistenciaTipoSchema = z.enum(['entrada', 'salida']);
export type AsistenciaTipo = z.infer<typeof AsistenciaTipoSchema>;

/**
 * Registro de asistencia de un participante a una sesión.
 */
export const AsistenciaSchema = z.object({
  id: EntityIdSchema,
  sesion_id: EntityIdSchema,
  participante_id: EntityIdSchema,
  tipo: AsistenciaTipoSchema,
  ts_dispositivo: IsoDateTimeSchema,
  ts_servidor: IsoDateTimeSchema.nullable().optional(),
  dispositivo_id: EntityIdSchema,
  operador_id: EntityIdSchema,
  emergencia_id: EntityIdSchema.nullable().optional(),
  offline_origen: z.union([z.literal(0), z.literal(1)]).or(z.boolean()),
  created_at: IsoDateTimeSchema,
  editada_por_usuario_id: EntityIdSchema.nullable().optional(),
  editada_en: IsoDateTimeSchema.nullable().optional(),
  editada_motivo: z.string().nullable().optional(),
});
export type Asistencia = z.infer<typeof AsistenciaSchema>;

/**
 * Input para PATCH /api/v1/asistencias/:id (edición de horas).
 * El endpoint NUNCA acepta DELETE; UPDATE solo modifica los 3 campos `editada_*`.
 */
export const AsistenciaEditInputSchema = z.object({
  ts_dispositivo: IsoDateTimeSchema,
  motivo: z.string().min(1),
});
export type AsistenciaEditInput = z.infer<typeof AsistenciaEditInputSchema>;

/**
 * Input para POST /api/v1/asistencias/manual (alta manual por parte de un administrador).
 */
export const AsistenciaManualInputSchema = z.object({
  sesion_id: EntityIdSchema,
  participante_id: EntityIdSchema,
  tipo: AsistenciaTipoSchema,
  ts_dispositivo: IsoDateTimeSchema,
  motivo: z.string().trim().min(3),
});
export type AsistenciaManualInput = z.infer<typeof AsistenciaManualInputSchema>;

/**
 * Item de lista de asistencia para el panel admin.
 * Incluye datos normalizados del participante y sesión.
 *
 * UNA fila por PERSONA: la entrada y la salida son dos acciones de la misma
 * persona, así que van en el mismo item (`ts_dispositivo` = entrada,
 * `ts_salida` = salida). Antes había una fila por marca y la misma persona
 * aparecía duplicada.
 */
export const AsistenciaListaItemSchema = z.object({
  // Datos de asistencia
  asistencia_id: EntityIdSchema,
  tipo: AsistenciaTipoSchema, // 'entrada' | 'salida'
  ts_dispositivo: IsoDateTimeSchema,
  ts_servidor: IsoDateTimeSchema.nullable().optional(),
  // Marca de SALIDA de la misma persona (null si no escaneó salida)
  salida_asistencia_id: EntityIdSchema.nullable(),
  ts_salida: IsoDateTimeSchema.nullable(),
  // Datos del participante (id de la fila CANÓNICA: la alias se resuelve)
  participante_id: EntityIdSchema,
  nombre: z.string(),
  apellido: z.string(),
  cedula: z.string(), // normalizada: V.XXX.XXX
  telefono: z.string().nullable(),
  email: z.string().nullable(),
  // Datos de datos_inscripcion
  pnf: z.string().nullable(),
  sede: z.string().nullable(),
  nivel_instruccion: z.string().nullable(),
  condicion_docente: z.string().nullable(),
  // Datos de la sesión
  sesion_id: EntityIdSchema,
  sesion_fecha: z.string(),
  sesion_hora_inicio: z.string(),
  sesion_hora_fin: z.string(),
  sesion_tema: z.string().nullable(),
  unidad_curricular: z.string().nullable(), // nombre de la unidad curricular
  // Puntualidad calculada: "tarde" es llegar después del 50 % de la charla
  // (contado desde el inicio real, que es el primer escaneo).
  // `null` = la persona no tiene entrada (solo salida).
  // Opcional: el panel no debe romperse contra un backend que aún no la manda.
  puntualidad: z.enum(['puntual', 'tarde']).nullable().optional(),
  /**
   * @deprecated Usar `puntualidad`. Se conserva porque el bundle del panel
   * que está desplegado HOY exige este campo en su schema: sin él, la lista de
   * asistencia del panel de producción deja de parsear durante la ventana
   * entre el deploy del backend y el del frontend. Mismo significado:
   * 'presente' = puntual, 'tarde' = tarde, null = sin entrada.
   */
  estado: z.enum(['presente', 'tarde']).nullable().optional(),
});
export type AsistenciaListaItem = z.infer<typeof AsistenciaListaItemSchema>;

/**
 * Response de GET /api/v1/sesiones/:id/asistencia-lista
 *
 * `participantes_total` = personas con al menos una marca.
 * `entradas_count` / `salidas_count` = personas con entrada / con salida.
 * `marcas_total` = filas crudas de `asistencia` (entradas + salidas); puede ser
 * mayor que `participantes_total` si alguien escaneó más de una vez.
 *
 * La ventana real de la sesión (para que el panel pueda explicar el criterio):
 * - `sesion_inicio_real` / `sesion_fin_real`: hora local ("HH:MM"). El inicio
 *   real es el primer escaneo y el fin se desplaza lo mismo.
 * - `sesion_umbral_tarde`: hora local ("HH:MM") a partir de la cual se marca
 *   "Tarde" (50 % de la charla).
 * - `sesion_cierre_automatico`: instante (ISO) en que la sesión se cierra sola
 *   si el operador no la cierra a mano (fin real + 10 min).
 *
 * Los cuatro son OPCIONALES: el panel no debe romperse si el backend todavía
 * no los manda (y al revés, el bundle viejo del panel usa el alias `estado`).
 */
export const AsistenciaListaResponseSchema = z.object({
  sesion_id: EntityIdSchema,
  sesion_fecha: z.string(),
  sesion_tema: z.string().nullable(),
  sesion_estado: z.string(),
  sesion_inicio_real: z.string().optional(),
  sesion_fin_real: z.string().optional(),
  sesion_umbral_tarde: z.string().optional(),
  sesion_cierre_automatico: z.string().optional(),
  cohorte_id: EntityIdSchema.optional(),
  cohorte_nombre: z.string().nullable(),
  programa_nombre: z.string().nullable(),
  participantes_total: z.number().int(),
  entradas_count: z.number().int(),
  salidas_count: z.number().int(),
  marcas_total: z.number().int(),
  data: z.array(AsistenciaListaItemSchema),
});
export type AsistenciaListaResponse = z.infer<typeof AsistenciaListaResponseSchema>;